import React, { useCallback, useEffect, useState } from 'react';
import { Ticket, Euro, Loader2, ShoppingCart, Mail, ArrowLeft, Plus, Minus, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { PRICES } from '../config/prices';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// Preset cards describe what the voucher would unlock in plain language —
// a recipient who got the code as a gift shouldn't have to know that "L"
// means anything specific.
const PRESET_AMOUNTS_CENTS = [
  { label: 'Digital Size M Image', sub: 'eg. 40x30cm for Format 4:3', cents: Math.round((PRICES.M || 2.49) * 100) },
  { label: 'Digital Size L Image', sub: 'eg. 80x60cm for Format 4:3', cents: Math.round((PRICES.L || 4.49) * 100) },
];

const MIN_CUSTOM_AMOUNT_CENTS = 500;

const PublicBuyVoucherDialog = ({ open, onOpenChange }) => {
  const [email, setEmail] = useState('');
  // Explicit mode toggle so clicking the custom card switches selection
  // even if the user hasn't changed the amount yet — relying on
  // `customAmountInput` truthiness alone meant the card looked deselected
  // until the user typed a digit.
  const [mode, setMode] = useState('preset'); // 'preset' | 'custom'
  const [selectedAmountCents, setSelectedAmountCents] = useState(PRESET_AMOUNTS_CENTS[1].cents);
  const [customAmountInput, setCustomAmountInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // 'buy' shows the amount cards + email + submit. 'browse' switches
  // to a list of every print product × size with the live Prodigi
  // price so the buyer can pick a voucher amount that exactly covers
  // a specific print they want to gift / use themselves.
  const [view, setView] = useState('buy');
  const [browseItems, setBrowseItems] = useState(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState(null);

  // Reset view + error whenever the dialog re-opens so a previous
  // attempt doesn't linger after the user closed and re-opened it.
  useEffect(() => {
    if (open) {
      setView('buy');
      setError(null);
    }
  }, [open]);

  const fetchBrowseItems = useCallback(async () => {
    setBrowseLoading(true);
    setBrowseError(null);
    try {
      // Public bulk endpoint — no JWT required. Returns all
      // (product, tier) combinations with the marked-up item price,
      // sorted ascending. Backed by the same 24h Prodigi quote cache
      // the gated /prodigi/quote route uses.
      const r = await fetch(`${API_URL}/public/prodigi/prices`);
      if (!r.ok) throw new Error('Failed to load print prices');
      const data = await r.json();
      setBrowseItems(data.items || []);
    } catch (e) {
      setBrowseError(e.message || 'Failed to load print prices');
    } finally {
      setBrowseLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'browse' && browseItems === null && !browseLoading) {
      fetchBrowseItems();
    }
  }, [view, browseItems, browseLoading, fetchBrowseItems]);

  const selectBrowseItem = (item) => {
    setMode('custom');
    // Use the print's exact price (cents-precision) so the voucher
    // matches the print at checkout — e.g. €15.99, not €16.
    setCustomAmountInput((item.item_price || 0).toFixed(2));
    setView('buy');
  };

  const handleSubmit = async () => {
    setError(null);
    let amount = selectedAmountCents;
    const isCustom = mode === 'custom';
    if (isCustom) {
      const parsed = Math.round(parseFloat(customAmountInput) * 100);
      if (Number.isFinite(parsed) && parsed > 0) {
        amount = parsed;
      } else {
        amount = 0;
      }
    }
    if (!amount || amount <= 0) {
      setError('Pick a preset or enter a custom amount.');
      return;
    }
    if (isCustom && amount < MIN_CUSTOM_AMOUNT_CENTS) {
      setError('Custom amounts start at €5.00.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError('Enter a valid email — your voucher code is delivered there.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/public/buy-voucher`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_cents: amount, email: email.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to start checkout');
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      throw new Error('Checkout URL missing');
    } catch (e) {
      setError(e.message || 'Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Match the map purchase popup's responsive sizing:
          - Mobile: 90vw so the dialog has breathing room from the
            viewport edges, with corners rounded at every size.
          - sm+: dynamic up to max-w-md (the dialog's natural width). */}
      <DialogContent className="bg-white rounded-2xl p-0 overflow-hidden w-[90vw] sm:w-full max-w-md">
        <div className="bg-gradient-to-br from-gray-900 to-gray-700 text-white px-6 py-5 flex items-center gap-3">
          {view === 'browse' ? (
            <button
              type="button"
              onClick={() => setView('buy')}
              className="h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
              title="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          ) : (
            <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center">
              <Ticket className="h-5 w-5" />
            </div>
          )}
          <div className="flex-1">
            <DialogTitle className="text-lg font-semibold">
              {view === 'browse' ? 'Browse print prices' : 'Buy a voucher'}
            </DialogTitle>
            <p className="text-xs text-gray-300 mt-0.5">
              {view === 'browse'
                ? 'Pick a print — its price becomes the voucher amount.'
                : 'Pay once, redeem anytime. Codes can be shared with friends.'}
            </p>
          </div>
        </div>

        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto space-y-4">
          {view === 'browse' ? (
            <section className="space-y-2">
              {browseLoading && (
                <div className="flex items-center justify-center py-10 text-gray-500">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading print prices…
                </div>
              )}
              {browseError && (
                <div className="text-sm text-red-500 text-center py-4">{browseError}</div>
              )}
              {!browseLoading && !browseError && browseItems && browseItems.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-8">
                  No print prices available right now.
                </div>
              )}
              {!browseLoading && browseItems && browseItems.length > 0 && (
                <div className="space-y-1.5">
                  {browseItems.map((item) => (
                    <button
                      key={`${item.product_id}-${item.tier}`}
                      type="button"
                      onClick={() => selectBrowseItem(item)}
                      className="w-full p-3 rounded-xl border border-gray-200 hover:bg-gray-50 text-sm flex items-center justify-between gap-3 transition-colors"
                    >
                      <div className="text-left min-w-0">
                        <div className="font-medium truncate">{item.product_label}</div>
                        <div className="text-[11px] text-gray-500 truncate">
                          Size {item.tier} · {item.cm} cm
                        </div>
                      </div>
                      <div className="font-semibold flex-shrink-0">€{(item.item_price || 0).toFixed(2)}</div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          ) : (
            <>
              <section className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <Mail className="h-3.5 w-3.5" /> Email
                </div>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="w-full bg-yellow-100"
                />
                <p className="text-[11px] text-gray-500 text-center">
                  Your voucher code is shown on-screen after payment and emailed to you.
                </p>
              </section>

              <section className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <ShoppingCart className="h-3.5 w-3.5" /> Amount
                </div>
                {/* 2x2 grid: M / L / Browse / Custom. Each card has
                    the price/headline centered on the first line, then
                    the title and a one-line description. */}
                <div className="grid grid-cols-2 gap-2">
                  {PRESET_AMOUNTS_CENTS.map((p) => {
                    const isSelected = mode === 'preset' && selectedAmountCents === p.cents;
                    return (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => { setMode('preset'); setSelectedAmountCents(p.cents); }}
                        className={`p-3 rounded-xl border text-sm transition-all text-center ${
                          isSelected
                            ? 'border-gray-900 bg-gray-900 text-white shadow-sm'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <div className="text-base font-semibold">€{(p.cents / 100).toFixed(2)}</div>
                        <div className="mt-1 text-sm font-medium">{p.label}</div>
                        <div className={`text-[11px] mt-0.5 ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>{p.sub}</div>
                      </button>
                    );
                  })}

                  {/* Browse prints — opens the sub-view that lists every
                      product × size with the live Prodigi price. Not a
                      selectable preset itself; clicking inside the
                      browse view is what actually sets the amount. */}
                  <button
                    type="button"
                    onClick={() => setView('browse')}
                    className="p-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm text-center transition-all"
                  >
                    <div className="text-base font-semibold flex items-center justify-center gap-1">
                      <Search className="h-4 w-4" /> Browse
                    </div>
                    <div className="mt-1 text-sm font-medium">Browse prints</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">See all sizes and prices</div>
                  </button>

                  {(() => {
                    // Custom-amount card. When `mode === 'custom'` the
                    // card flips to a black background; the input and
                    // ± buttons stay white pills so they remain
                    // readable on the dark background.
                    const isSelected = mode === 'custom';
                    const adjustCustom = (delta) => {
                      setMode('custom');
                      const parsed = parseFloat(customAmountInput);
                      const base = Number.isFinite(parsed) && parsed > 0 ? parsed : 5;
                      const next = Math.max(5, Math.round(base + delta));
                      setCustomAmountInput(String(next));
                    };
                    return (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setMode('custom')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode('custom'); } }}
                        className={`p-3 rounded-xl border text-sm transition-all cursor-pointer text-center ${
                          isSelected
                            ? 'border-gray-900 bg-gray-900 text-white shadow-sm'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <div
                          className="flex items-center justify-center gap-1.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => adjustCustom(-5)}
                            title="-€5"
                            className="h-7 w-7 rounded-full flex items-center justify-center bg-white text-gray-900 shadow-sm hover:bg-gray-100 transition-colors"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <div className="flex items-center bg-white text-gray-900 rounded-xl px-2 h-8">
                            <Euro className="h-3.5 w-3.5 text-gray-400 mr-1" />
                            <Input
                              type="number"
                              min="5"
                              step="0.01"
                              placeholder="5"
                              value={customAmountInput}
                              onChange={(e) => setCustomAmountInput(e.target.value)}
                              onFocus={() => setMode('custom')}
                              className="w-16 border-0 p-0 h-auto bg-transparent text-sm font-semibold text-center text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => adjustCustom(5)}
                            title="+€5"
                            className="h-7 w-7 rounded-full flex items-center justify-center bg-white text-gray-900 shadow-sm hover:bg-gray-100 transition-colors"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="mt-1 text-sm font-medium">Custom amount</div>
                        <div className={`text-[11px] mt-0.5 ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>Min €5</div>
                      </div>
                    );
                  })()}
                </div>
              </section>

              {error && <p className="text-xs text-red-500 text-center">{error}</p>}

              <div className="flex justify-center pt-1">
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-gray-900 hover:bg-gray-700 text-white border-0 shadow-md text-sm font-semibold"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
                  ) : (
                    <><ShoppingCart className="h-4 w-4 mr-2" /> Continue to payment</>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PublicBuyVoucherDialog;
